import { datadogRum } from '@datadog/browser-rum';
import { useSession } from '@side/next-auth/react';
import { isBefore } from 'date-fns';
import { useEffect } from 'react';
import { AGENT_TEAM_MEMBER_FILTER } from '@/common/constants/identityFilters';
import { useLeadAgents } from '@/common/hooks/useLeadScope/useLeadScope';
import type { IdentityRequest } from '$api-clients/hooks';
import { useIdentity } from '$api-clients/hooks';
import { Scopes } from '$authentication/useHasPermission/constants';
import { useHasPermission } from '$authentication/useHasPermission/useHasPermission';
import { RESTRICTED_STATES_BY_FEATURE } from './constants';
import type { AppFeature } from './types';

type SubmitAsTeamMember = NonNullable<
  IdentityRequest<'/users/{userId}/interaction-permissions/{type}/team-members'>['Data']['data']
>[number];
type UserInfoTeam = NonNullable<
  IdentityRequest<'/auth/userinfo'>['Data']['teams']
>[number];
type IdentityLicense = SubmitAsTeamMember['license'];

const isLicenseExpired = (license: NonNullable<IdentityLicense>) => {
  if (!license.expiration) return true;

  return isBefore(new Date(license.expiration), new Date());
};

// Server-side counterpart of the submit-as license check above: the lead-scope
// read only needs to know whether at least one qualifying agent exists.
const buildLeadLicenseQuery = (
  restrictedStates: ReadonlySet<string> | undefined,
) => {
  const clauses = [
    'status[in]=[ACTIVE,ONBOARDING,OFFBOARDING]',
    `license.expiration[gte]=${new Date().toISOString().slice(0, 10)}`,
  ];
  if (restrictedStates && restrictedStates.size > 0) {
    clauses.push(`license.state[nin]=[${[...restrictedStates].join(',')}]`);
  }
  return clauses.join(' & ');
};

/**
 * Declarative replacement for the previous effect-driven implementation.
 *
 * The old version fired `identityApi.GET('/auth/userinfo')` + a `submit-as`
 * team-members call imperatively inside a `useEffect` keyed on the `user`
 * object. Those imperative calls bypassed SWR's cache/deduping, so every
 * session refetch (which produces a new `user` reference) and every mounted
 * consumer issued fresh network requests — the dominant contributor to TXM's
 * request bursts.
 *
 * Routing both reads (and the lead-scope agents read) through `useIdentity`
 * means all consumers share one cache entry per key and re-renders/remounts
 * are served from cache. The
 * decision is derived synchronously from the cached data; nothing fetches in
 * an effect. Mirrors audit-ui's `useHasAccessToFeatureSwr`.
 *
 * Only georestricted features (currently just `LEASES`) are gated here; any
 * other feature has no access, matching the previous `default: false` switch
 * branch. Returns `undefined` while a required upstream read is still loading
 * so callers can render a stable "checking" state.
 */
export function useHasAccessToFeature(
  feature: AppFeature,
): boolean | undefined {
  const {
    hasPermission: hasInteractionsReadPermission,
    loading: interactionsReadLoading,
  } = useHasPermission({
    roles: ['ADMIN'],
    scopes: [Scopes.INTERACTIONS_READ],
  });

  // Read synchronously for the decision — NOT as an effect dependency, so an
  // unstable `user` reference can no longer trigger refetches.
  const user = useSession().data?.user;
  const userId = user?.id;
  const roles = user?.roles ?? [];

  const restrictedStates =
    RESTRICTED_STATES_BY_FEATURE[
      feature as keyof typeof RESTRICTED_STATES_BY_FEATURE
    ];

  const directAccess =
    roles.includes('ADMIN') ||
    roles.includes('AUDITOR') ||
    hasInteractionsReadPermission;

  // Only the georestricted path needs the license lookups. Admins/auditors and
  // non-georestricted features short-circuit before any network call — so most
  // renders fetch nothing here.
  const needsLicenseCheck =
    Boolean(userId) && Boolean(restrictedStates) && !directAccess;

  const {
    data: userInfo,
    isLoading: userInfoLoading,
    error: userInfoError,
  } = useIdentity('/auth/userinfo', needsLicenseCheck ? undefined : null);

  const {
    data: submitAsResponse,
    isLoading: submitAsLoading,
    error: submitAsError,
  } = useIdentity(
    '/users/{userId}/interaction-permissions/{type}/team-members',
    needsLicenseCheck && userId
      ? {
          params: {
            path: { userId, type: 'submit-as' },
            query: { limit: 500, q: AGENT_TEAM_MEMBER_FILTER },
          },
        }
      : null,
  );

  const lead = useLeadAgents({
    limit: 1,
    keepPreviousData: false,
    memberQuery: buildLeadLicenseQuery(restrictedStates),
    skip: !needsLicenseCheck,
  });

  // Without this, an API outage during the georestriction check is
  // indistinguishable from "user has no access" — the hook returns `false`
  // either way (data is undefined). Reporting the error preserves the
  // observability the old imperative implementation had.
  useEffect(() => {
    const error = userInfoError ?? submitAsError ?? lead.error;
    if (error !== undefined) {
      datadogRum.addError(error, {
        userId,
        message: 'Error determining geolocation restricted feature access',
      });
    }
  }, [userInfoError, submitAsError, lead.error, userId]);

  if (interactionsReadLoading) return undefined;
  if (!userId) return undefined;

  // Non-georestricted / unknown feature — parity with the old `default: false`
  // switch branch.
  if (!restrictedStates) return false;
  if (directAccess) return true;

  if (userInfoLoading || submitAsLoading) return undefined;

  const activeTeamIds = new Set(
    Object.keys(userInfo?.accessibleTeams?.nodes ?? {}),
  );
  const currentUserTeams: UserInfoTeam[] = userInfo?.teams ?? [];
  const currentUserActiveTeamMarkets = currentUserTeams
    .filter((team) => activeTeamIds.has(team.id))
    .map((team) => team.market);

  const submitAsTeamMembers: SubmitAsTeamMember[] =
    submitAsResponse?.data ?? [];

  const hasOwnAccess =
    submitAsTeamMembers.some(
      ({ license }) =>
        license &&
        !isLicenseExpired(license) &&
        !restrictedStates.has(license.state),
    ) ||
    currentUserActiveTeamMarkets.some(
      (market) => market && !restrictedStates.has(market),
    );
  if (hasOwnAccess) return true;

  if (lead.loading) return undefined;

  return lead.agents.length > 0;
}
